import { User, UserDomain, UserRole as UserRoleModel } from '../models/user.model';
import { UserRole, RoleLayer, DOMAIN_ROLE_HIERARCHY, ACCOUNT_LEVEL_ROLES } from '../../../core/auth/enums/role.enum';
import { Permission } from '../../../core/auth/enums/permission.enum';
import { UserAccessProfile, DomainAccess, RoleAssignment } from '../../../core/auth/interfaces/rbac.interface';
import { combinePermissions } from '../../../core/auth/config/role-permission.config';

export function buildUserAccessProfile(
  user: User | null,
  currentDomainId: string | null
): UserAccessProfile | null {
  if (!user) {
    return null;
  }

  const allRoles = normalizeRoles(user.roles ?? []);
  const accountRole = getAccountRole(allRoles);

  // All roles assigned at account layer — these apply across every domain
  const accountLevelRoles = allRoles.filter((role) => role.layer === RoleLayer.ACCOUNT);
  const accountLevelPermissions = combinePermissions(accountLevelRoles.map((role) => role.code));

  const domainAccess = buildDomainAccessMap(user.domains ?? [], allRoles, accountLevelPermissions);

  const activeDomainAccess = currentDomainId
    ? domainAccess.get(currentDomainId) ?? null
    : null;

  return {
    userUuid: user.uuid,
    allRoles,
    accountRole,
    domainAccess,
    activeDomainAccess,
    globalPermissions: getGlobalPermissions(accountLevelPermissions, domainAccess),
  };
}

function normalizeRoles(roles: readonly UserRoleModel[]): RoleAssignment[] {
  return roles.map((role) => ({
    id: role.id,
    code: role.code as UserRole,
    name: role.name,
    layer: role.layer as RoleLayer,
    domainId: role.domainId,
    domainUuid: role.domainUuid,
  }));
}

function getAccountRole(roles: readonly RoleAssignment[]): UserRole | undefined {
  return roles.find((role) =>
    role.layer === RoleLayer.ACCOUNT &&
    ACCOUNT_LEVEL_ROLES.includes(role.code)
  )?.code;
}

function buildDomainAccessMap(
  domains: readonly UserDomain[],
  roles: readonly RoleAssignment[],
  accountLevelPermissions: Permission[]
): Map<string, DomainAccess> {
  const domainRolesByUuid = groupDomainRolesByUuid(roles, domains);

  return new Map(
    domains.map((domain) => [
      domain.uuid,
      buildDomainAccess(domain, domainRolesByUuid.get(domain.uuid) ?? [], accountLevelPermissions),
    ])
  );
}

function groupDomainRolesByUuid(
  roles: readonly RoleAssignment[],
  domains: readonly UserDomain[]
): Map<string, RoleAssignment[]> {
  return roles.reduce((groupedRoles, role) => {
    if (role.layer !== RoleLayer.DOMAIN) {
      return groupedRoles;
    }

    // Resolve domainUuid from domainId if not directly provided
    const domainUuid = role.domainUuid
      ?? domains.find((domain) => domain.id === role.domainId)?.uuid;

    if (!domainUuid) {
      return groupedRoles;
    }

    const domainRoles = groupedRoles.get(domainUuid) ?? [];

    groupedRoles.set(domainUuid, [...domainRoles, { ...role, domainUuid }]);

    return groupedRoles;
  }, new Map<string, RoleAssignment[]>());
}

function buildDomainAccess(
  domain: UserDomain,
  roles: readonly RoleAssignment[],
  accountLevelPermissions: Permission[]
): DomainAccess {
  const roleCodes = roles.map((role) => role.code);
  const domainPermissions = combinePermissions(roleCodes);

  // Merge account-level permissions — they override/extend any per-domain config
  const permissions = accountLevelPermissions.length > 0
    ? [...new Set([...accountLevelPermissions, ...domainPermissions])]
    : domainPermissions;

  return {
    domainUuid: domain.uuid,
    domainName: domain.name,
    domainId: domain.id,
    roles: [...roles],
    permissions,
    primaryRole: getPrimaryDomainRole(roles),
  };
}

function getPrimaryDomainRole(roles: readonly RoleAssignment[]): UserRole {
  return roles.reduce<UserRole>((highestRole, role) => {
    const roleLevel = DOMAIN_ROLE_HIERARCHY[role.code] ?? -1;
    const highestLevel = DOMAIN_ROLE_HIERARCHY[highestRole] ?? -1;

    return roleLevel > highestLevel ? role.code : highestRole;
  }, UserRole.NO_ACCESS);
}

function getGlobalPermissions(
  accountLevelPermissions: Permission[],
  domainAccess: ReadonlyMap<string, DomainAccess>
): Permission[] {
  return [
    ...new Set([
      ...accountLevelPermissions,
      ...Array.from(domainAccess.values()).flatMap((access) => access.permissions),
    ]),
  ];
}
